import { useState, useCallback, useEffect, useMemo } from "react";
import { format, addDays, subDays, addMonths, subMonths } from "date-fns";
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, DragMoveEvent, rectIntersection } from "@dnd-kit/core";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, CalendarClock, Check, ChevronLeft, ChevronRight, Circle, Clock, ListChecks, Wrench, XIcon } from "lucide-react";
import { useDrag, usePlanner } from "../contexts/PlannerContext";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { useCourses } from "@/lib/courses";
import { DueView } from './DueView';
import { PlannerView } from './PlannerView';
import { AssignmentPlanned, defaultTaskValues } from '@/lib/types';
import { RestrictedButton, RestrictedIcon, UnrestrictedButtonView, RestrictedButtonView } from "@/lib/restrictions";
import { UNRESTRICTED_LEARNPLUS_TRIAL } from "@/lib/restrictions";
import { calculateDragPosition, getDragType } from '../utils/dragDropUtils';
import { useCommunication } from "@/lib/CommunicationWrapper";
import { useAuth } from "@/lib/auth";
import { LimitTracker } from "./LimitTracker";

export const AssignmentsPageContent = () => {
    const { settings, modifyPlanned, scrollPos, findClosestAvailableTime, blockHeight, blockWidth, timeWidth, blockMargin, subBlocks, subBlockMinutes, subBlockHeight, cursorType, setCursorType, planned } = usePlanner();
    const { currentDragDelta, setCurrentDragDelta, activeDragItem, setActiveDragItem, resizeHeight, setResizeHeight, activeCalendarDragItem, setActiveCalendarDragItem, setShouldAnimateDrop, dropZone, setDropZone } = useDrag();

    // Store the initial click offset within the dragged element
    const [initialClickOffset, setInitialClickOffset] = useState<{ x: number; y: number; timeOffset?: number } | null>(null);

    const { showError, trackEvent, restrictedActionCheck, openRestrictedDialog, checkGate } = useCommunication();

    const handleClose = () => {
        trackEvent("assignments_closed");
    }

    useEffect(() => {
        // Set it immediately on mount
        document.body.setAttribute("data-dragging", "true");
        window.addEventListener('beforeunload', handleClose);
        return () => {
            // Clean up on unmount
            document.body.removeAttribute("data-dragging");
            window.removeEventListener('beforeunload', handleClose);

        };
    }, []);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
        useSensor(TouchSensor),
    );

    const handleDragStart = (event: DragStartEvent) => {
        // Store drag data for later use
        const planner = event.active.data.current?.planner;
        const dragType = getDragType(event.active.data.current);
        const isTasksView = (settings?.assignments_view || "planner") === "due";

        // Don't set activeCalendarDragItem immediately to prevent flicker
        // It will be set in handleDragMove when actually moving

        // Set appropriate cursor
        const cursor = dragType.includes('resize') ? 'ns-resize' : 'grabbing';
        setCursorType(cursor);
        setShouldAnimateDrop(isTasksView); // allow animation only on tasks view

        if (dragType === "due-item" || dragType === "unassigned-task") {
            setActiveDragItem(planner);
        } else if ((dragType === "calendar-item" || dragType === "calendar-item-resize-top" || dragType === "calendar-item-resize-bottom") && planner?.plannedAt) {
            // Seed preview state immediately for planner items so the ghost stays visible in day view
            setActiveCalendarDragItem(planner);
            setCurrentDragDelta(new Date(planner.plannedAt));
        }
    };

    const resetDragState = () => {
        setResizeHeight(null);
        setActiveDragItem(null);
        setActiveCalendarDragItem(null);
        setCurrentDragDelta(null);
        setDropZone(null);
        setCursorType(null);
        setInitialClickOffset(null);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const isTasksView = (settings?.assignments_view || "planner") === "due";
        let shouldAnimateDropFlag = isTasksView;

        try {

            const planner: AssignmentPlanned = event.active.data.current?.planner || {};
            const dragType = getDragType(event.active.data.current);
            const overType = event.over?.data?.current?.type;
            const overDate = event.over?.data?.current?.date ? new Date(event.over.data.current.date) : null;

            let targetDate = currentDragDelta;
            if (!targetDate && overType === "month-day" && overDate) {
                targetDate = overDate;
            }

            if (!event.over || !targetDate) {
                return;
            }

            // Calculate the updates based on drag type
            let updates: Partial<AssignmentPlanned> | null = null;

            if (resizeHeight) {
                // Handle resize
                const duration = Math.round(resizeHeight / subBlockHeight) * subBlockMinutes;
                updates = findClosestAvailableTime(planner, duration, targetDate);
            } else {
                // Handle move
                let desiredTime = targetDate;

                // For month view drops, seed the desired time using auto-plan preferences on that day
                if (dragType === "unassigned-task" && overType === "month-day") {
                    const { assignments_auto_plan = { time: "morning" } } = settings || {};
                    const timingPreferences: { [key: string]: number } = {
                        "morning": 10,
                        "afternoon": 14,
                        "evening": 18,
                        "night": 22,
                    };
                    const recommendedHour = timingPreferences[assignments_auto_plan.time] ?? 10;
                    const seeded = new Date(targetDate);
                    seeded.setHours(recommendedHour, 0, 0, 0);
                    desiredTime = seeded;
                }

                updates = findClosestAvailableTime(planner, null, desiredTime);
            }

            console.log("[handleDragEnd] updates", updates);

            if (!updates) {
                showError("Couldn't find a spot for this assignment on that day.", "error");
            } else {
                const initialPlannedAt = planner?.plannedAt ?? null;
                const nextPlannedAt = updates?.plannedAt ?? initialPlannedAt ?? null;

                const initialPlannedTime = initialPlannedAt ? new Date(initialPlannedAt).getTime() : null;
                const nextPlannedTime = nextPlannedAt ? new Date(nextPlannedAt).getTime() : null;

                const becamePlanned = initialPlannedTime === null && nextPlannedTime !== null;
                const plannedTimeChanged = initialPlannedTime !== null && nextPlannedTime !== null && initialPlannedTime !== nextPlannedTime;

                // Tasks page only: animate back when nothing changes; otherwise snap
                if (isTasksView) {
                    shouldAnimateDropFlag = !(becamePlanned || plannedTimeChanged);
                } else {
                    shouldAnimateDropFlag = false;
                }
                setShouldAnimateDrop(shouldAnimateDropFlag);

                const success = await modifyPlanned({
                    ...planner,
                    ...updates,
                });

                if (!success) {
                    // Overlap or validation error already surfaced to the user
                    shouldAnimateDropFlag = false;
                    setShouldAnimateDrop(false);
                    return;
                }

                // Track analytics
                //withEventCooldown("assignment_planned", async () => {
                await trackEvent("assignment_planned");
                //}, 5);
            }
        } catch (error) {
            console.error("Error during drag end:", error);
            showError("An unexpected error occurred. Please try again.", "error");
        } finally {
            setShouldAnimateDrop(isTasksView ? shouldAnimateDropFlag : false);
            resetDragState();
        }
    };

    const handleDragCancel = () => {
        setShouldAnimateDrop(false);
        resetDragState();
    };

    const handleDragMove = (event: DragMoveEvent) => {
        if (!event.over) return;

        const dragType = getDragType(event.active?.data?.current);
        const planner = event.active.data.current?.planner || {};
        const overType = event.over.data.current?.type;
        const overDate = event.over.data.current?.date ? new Date(event.over.data.current.date) : null;

        // Handle drag over non-calendar areas
        //if (overType !== "calendar-column") {
        if (dragType === "due-item" || dragType === "unassigned-task") {
            if (activeDragItem?.id !== planner?.id) {
                setActiveDragItem(planner);
            }
            /*
            if (currentDragDelta) {
                setCurrentDragDelta(null);
            }
            if (activeCalendarDragItem?.id) {
                setActiveCalendarDragItem(null);
            }
                */
            if (cursorType !== "grabbing") {
                setCursorType("grabbing");
            }

        }

        // Get scroll offset for items being dragged from outside the calendar
        const scrollOffset = (dragType === 'due-item' || dragType === 'unassigned-task') ? (scrollPos.current?.top || 0) : 0;

        if (dragType === 'due-item') {
            console.log('Passing scroll offset to calculateDragPosition:', scrollOffset);
        }

        if (overType === "calendar-column") {

            // Calculate drag position using our utility
            const result = calculateDragPosition(event, dragType, {
                blockHeight,
                subBlockHeight,
                subBlockMinutes,
                subBlocks,
                planner,
                scrollOffset
            });

            // Update cursor
            if (cursorType !== result.cursorType) {
                setCursorType(result.cursorType);
            }

            // Update drag states
            if (result.date && (!currentDragDelta || currentDragDelta.getTime() !== result.date.getTime())) {
                setCurrentDragDelta(result.date);
            }

            if (result.resizeHeight !== undefined && resizeHeight !== result.resizeHeight) {
                setResizeHeight(result.resizeHeight);
            }

            // Update active item states
            if (activeCalendarDragItem?.id !== planner?.id) {
                setActiveCalendarDragItem(planner);
            }
        } else if (overType === "month-day") {
            if (overDate && (!currentDragDelta || currentDragDelta.getTime() !== overDate.getTime())) {
                setCurrentDragDelta(overDate);
            }
            if (activeCalendarDragItem?.id !== planner?.id) {
                setActiveCalendarDragItem(planner);
            }
            setDropZone({ type: "month-day", date: overDate?.toDateString() });
            if (cursorType !== "grabbing") setCursorType("grabbing");
        } else {
            if (activeCalendarDragItem !== null) {
                setActiveCalendarDragItem(null);
            }
            if (currentDragDelta !== null) {
                setCurrentDragDelta(null);
            }
            if (dropZone !== null) {
                setDropZone(null);
            }
        }

        /*
        if (activeDragItem) {
            setActiveDragItem(null);
        }
            */
    };

    return (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            onDragMove={handleDragMove}
            collisionDetection={rectIntersection}
            autoScroll={{
                enabled: false,
                threshold: { x: 0, y: 0 },
            }}>
            <div className="h-screen flex flex-col overflow-hidden bg-theme-0 text-theme-0 font-theme">
                <PlannerHeader />
                {settings.assignments_view === "due" ? <DueView /> : <PlannerView />}
                <LimitTracker />
            </div>
        </DndContext>
    );
};

export const PlannerDateChanger = () => {
    const { startDate, setStartDate, settings } = usePlanner();
    const { checkGate } = useCommunication();
    const today = new Date();
    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;

    const isCurrentPeriod = useMemo(() => {
        if (calendarViewType === "month") {
            return startDate.getFullYear() === today.getFullYear() && startDate.getMonth() === today.getMonth();
        }
        return startDate.getFullYear() === today.getFullYear() && startDate.getMonth() === today.getMonth() && startDate.getDate() === today.getDate();
    }, [startDate, today, calendarViewType]);

    const onPrevious = useCallback(async () => {
        // Calculate what the new date would be
        let newDate;
        if (calendarViewType === "day") {
            newDate = subDays(startDate, 1);
        } else if (calendarViewType === "week" || calendarViewType === "5day") {
            newDate = subDays(startDate, effectiveWeekDays);
        } else if (calendarViewType === "month") {
            newDate = subMonths(startDate, 1);
        }

        const todayNormalized = new Date(today);
        todayNormalized.setHours(0, 0, 0, 0);

        // For month view: only allow current month
        if (calendarViewType === "month") {
            const isLeavingCurrentMonth = newDate.getMonth() !== todayNormalized.getMonth() ||
                                         newDate.getFullYear() !== todayNormalized.getFullYear();
            if (isLeavingCurrentMonth) {
                const { restricted } = await checkGate("assignments_calendar_date_change");
                if (restricted) return;
            }
        } else {
            // For week/day view: allow up to 2 weeks (14 days) back
            const twoWeeksBeforeToday = subDays(todayNormalized, 14);
            if (newDate < twoWeeksBeforeToday) {
                const { restricted } = await checkGate("assignments_calendar_date_change");
                if (restricted) return;
            }
        }

        setStartDate(newDate);
    }, [calendarViewType, effectiveWeekDays, startDate, today, checkGate, setStartDate]);

    const onNext = useCallback(async () => {
        // Calculate what the new date would be
        let newDate;
        if (calendarViewType === "day") {
            newDate = addDays(startDate, 1);
        } else if (calendarViewType === "week" || calendarViewType === "5day") {
            newDate = addDays(startDate, effectiveWeekDays);
        } else if (calendarViewType === "month") {
            newDate = addMonths(startDate, 1);
        }

        const todayNormalized = new Date(today);
        todayNormalized.setHours(0, 0, 0, 0);

        // For month view: only allow current month
        if (calendarViewType === "month") {
            const isGoingToNextMonth = newDate.getMonth() !== todayNormalized.getMonth() ||
                                      newDate.getFullYear() !== todayNormalized.getFullYear();
            if (isGoingToNextMonth) {
                const { restricted } = await checkGate("assignments_calendar_date_change");
                if (restricted) return;
            }
        } else {
            // For week/day view: allow up to 2 weeks (14 days) in advance
            const twoWeeksFromToday = addDays(todayNormalized, 14);
            if (newDate > twoWeeksFromToday) {
                const { restricted } = await checkGate("assignments_calendar_date_change");
                if (restricted) return;
            }
        }

        setStartDate(newDate);
    }, [calendarViewType, effectiveWeekDays, startDate, today, checkGate, setStartDate]);

    const getDisplayText = () => {
        if (calendarViewType === "month" || calendarViewType === "week" || calendarViewType === "5day") return format(startDate, "MMMM yyyy");
        return format(startDate, "MMM d, yyyy");
    };

    const onTodayClick = useCallback(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Always allow going back to today - it's within the free range
        setStartDate(today);
    }, [setStartDate]);

    return (
        <div className="flex items-center gap-4">
            <button
                onClick={onTodayClick}
                className="px-3 py-1 rounded-full text-sm font-medium text-theme-0 border border-theme hover:bg-theme-1 transition-colors"
            >
                Today
            </button>
            <div className="flex items-center gap-1">
                <button onClick={onPrevious} className="p-1.5 rounded-md hover:bg-theme-1 text-theme-1 transition-colors">
                    <ArrowLeft className="size-5" />
                </button>
                <button onClick={onNext} className="p-1.5 rounded-md hover:bg-theme-1 text-theme-1 transition-colors">
                    <ArrowRight className="size-5" />
                </button>
            </div>
            <span className="text-sm font-semibold text-theme-0 min-w-[100px]">{getDisplayText()}</span>
        </div>
    );
};

export const ViewChanger = () => {
    const { settings, updateSettings } = usePlanner();

    const setView = async (view: string) => {
        updateSettings({ assignments_view: view });
    };

    return (
        <div className="relative grid grid-cols-2 bg-theme-2 rounded-lg p-1 font-medium">
            {/* Sliding background */}
            <motion.div
                className="absolute top-1 bottom-1 bg-theme-0 rounded-md"
                initial={false}
                animate={{
                    left: settings?.assignments_view === "due" ? "4px" : "calc(50% + 2px)",
                    width: "calc(50% - 6px)",
                }}
                transition={{ type: "spring", stiffness: 400, damping: 40 }}
            />

            <button
                className={`flex items-center justify-center gap-1.5 cursor-pointer relative z-10 text-sm rounded-md px-3 py-1.5 transition-all duration-200 ${settings?.assignments_view === "due" ? "text-theme-0" : "text-theme-1 hover:bg-theme-1"
                    }`}
                onClick={() => setView("due")}
            >
                <ListChecks className="size-4" />
                To-Do
            </button>
            <button
                className={`flex items-center justify-center gap-1.5 cursor-pointer relative z-10 text-sm rounded-md px-3 py-1.5 transition-all duration-200 ${settings?.assignments_view === "planner" ? "text-theme-0" : "text-theme-1 hover:bg-theme-1"
                    }`}
                onClick={() => setView("planner")}
            >
                <Calendar className="size-4" />
                Calendar
            </button>
        </div>
    );
};

const SetupWizardBtn = () => {
    const { portal } = usePlanner();
    const { openRestrictedDialog, useRerenderListener, updateOptions, loadSharedComponent } = useCommunication();
    const { options } = useRerenderListener(["assignments_setup_complete", "google_calendars"]);
    const complete = options.assignments_setup_complete;
    const { courseData } = useCourses();
    const [syncOpen, setSyncOpen] = useState(false);
    const [classTimesOpen, setClassTimesOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [classTimesTaskDone, setClassTimesTaskDone] = useState(false);

    // Custom handler to prevent closing when nested dialogs are open
    const handleOpenChange = (newOpen: boolean) => {
        // Only allow closing if no nested dialogs are open
        if (!newOpen && (syncOpen || classTimesOpen)) {
            return;
        }
        setOpen(newOpen);
    };

    const calendarConnected = Object.keys(options?.google_calendars || {}).length > 0;
    const hasClassTimes = Object.values(courseData || {}).some((c: any) => Array.isArray(c?.schedule) && c.schedule.length > 0);

    // Mark class times task as done when user has class times and closes the dialog
    // Once set to true, it stays true
    useEffect(() => {
        if (!classTimesTaskDone && hasClassTimes && !classTimesOpen) {
            setClassTimesTaskDone(true);
        }
    }, [hasClassTimes, classTimesOpen, classTimesTaskDone]);

    const total = 2;
    const done = [calendarConnected, classTimesTaskDone].filter(Boolean).length;

    // Handle completion animation - only trigger when no nested dialogs are open
    useEffect(() => {
        if (done === total && !syncOpen && !classTimesOpen) {
            // Close the dialog with animation after a brief delay
            setTimeout(() => {
                setOpen(false);
                updateOptions({ assignments_setup_complete: true });
            }, 800);
        }
    }, [done, total, syncOpen, classTimesOpen]);

    const circleCheckIcon = (
        <div className="relative size-4.5">
            <div className="size-4.5 rounded-full bg-theme-link shrink-0" />
            <Check className="size-3.5 shrink-0 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ color: "var(--bcbackground-0, #fff)" }} />
        </div>
    );

    if (complete) return null;

    return (
        <div className="h-full flex items-center ">
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogTrigger>
                    <div className="cursor-pointer flex items-center gap-1.5 text-xs text-theme-0 border border-theme rounded-lg px-2 py-1">
                        <Wrench className="size-4 text-theme-1" />
                        <span className="whitespace-nowrap">
                            Finish setup {done} / {total}
                        </span>
                    </div>
                </DialogTrigger>
                <DialogContent container={portal} variant="theme" className="w-[300px] p-0 relative" showCloseButton={false}>
                    <DialogClose className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                        <XIcon className="text-base-1" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                    <div className="bg-theme-2 rounded-t-lg">
                        <div className="px-6 pt-5 pb-4">
                            <h3 className="text-lg text-theme-0">Finish your setup</h3>
                            <div className="flex items-center gap-2 text-sm text-theme-0 mt-3">
                                <span>
                                    {done} of {total}
                                </span>
                                <div className="ml-1 w-49 h-2.5 bg-theme-0 rounded-full border border-theme flex overflow-hidden">
                                    {Array.from({ length: total }).map((_, i) => (
                                        <div key={i} className={`flex-1 h-full ${i < done ? "bg-theme-link" : "bg-theme-0"} ${i < total - 1 ? "border-r-2 border-theme" : ""}`} />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="w-full h-px bg-theme-2" />
                    </div>
                    <div className="flex flex-col">
                        {/* CalendarSync */}
                        <div onClick={() => loadSharedComponent("CalendarSyncGate", { showCloseButton: true })} className="flex items-center justify-between py-4 px-6 -mt-4 cursor-pointer transition-all duration-200 hover:bg-theme-1">
                            <div className="flex items-center gap-3">
                                {calendarConnected ? circleCheckIcon : <Circle className="size-4.5 text-theme-1 shrink-0" />}
                                <p className={`text-base font-medium text-theme-0 ${calendarConnected && "line-through"}`}>Sync your calendar</p>
                            </div>
                            <ChevronRight className="size-6.5 text-theme-1 shrink-0" />
                        </div>

                        <div className="w-full h-px bg-theme-2" />

                        {/* Class Times */}
                        <div onClick={() => loadSharedComponent("ScheduleEditor")} className="flex items-center justify-between py-4 px-6 cursor-pointer transition-all duration-200 hover:bg-theme-1 rounded-b-lg">
                            <div className="flex items-center gap-3">
                                {classTimesTaskDone ? circleCheckIcon : <Circle className="size-4.5 text-theme-1 shrink-0" />}
                                <p className={`text-base font-medium text-theme-0 ${classTimesTaskDone && "line-through"}`}>Add your class times</p>
                            </div>
                            <ChevronRight className="size-6.5 text-theme-1 shrink-0 cursor-pointer" />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const PlannerHeader = () => {
    return (
        <div className="border-b border-theme h-[60px] px-4 flex justify-between items-center relative z-20 bg-theme-0">
            <ViewChanger />
            <SetupWizardBtn />
        </div>
    );
};

const OffsetScroller = ({ initValue }: { initValue: number }) => {
    const { autoScheduleSettings, setAutoScheduleSettings } = usePlanner();

    const [value, setValue] = useState(initValue);

    const min = 0;
    const max = 6;

    const left = value === min ? null : value - 1;
    const right = value === max ? null : value + 1;

    useEffect(() => {
        setAutoScheduleSettings({ ...autoScheduleSettings, offset: value });
    }, [value]);

    return (
        <div className="flex items-center gap-[5px] user-select-none">
            <button className="w-[25px] h-[25px] flex items-center justify-center">
                <ChevronLeft className="h-[16px] w-[16px]" onClick={() => setValue(left || 0)} />
            </button>
            <p className="user-select-none text-sm text-theme-1 w-[50px] text-center" style={{ opacity: left ? 1 : 0 }}>
                {left || `${min}`}
            </p>
            <p className="user-select-none text-sm text-black font-medium w-[50px] text-center">{value}</p>
            <p className="user-select-none text-sm text-theme-1 w-[50px] text-center" style={{ opacity: right ? 1 : 0 }}>
                {right || `${max}`}
            </p>
            <button className="w-[25px] h-[25px] flex items-center justify-center">
                <ChevronRight className="h-[16px] w-[16px]" onClick={() => setValue(right || 6)} />
            </button>
        </div>
    );
};

const AutoPlanVisualizer = () => {
    const { autoScheduleSettings } = usePlanner();

    const { spacing, timing, procrastination } = autoScheduleSettings;

    const width = procrastination === "even" ? 80 : spacing === "spread" ? 50 : 20;
    const height = timing === "all-day" ? "70%" : "25%";
    const left = procrastination === "even" ? 10 : procrastination === "asap" ? 0 : procrastination === "midpoint" ? 30 : 60;
    const top = timing === "all-day" ? 15 : timing === "morning" ? 15 : timing === "midday" ? 30 : timing === "afternoon" ? 45 : 60;

    return (
        <div>
            <div className="w-[150px]">
                <div className="w-full h-[70px] bg-theme-1 rounded-md p-[3px] relative">
                    <div className="transition-all duration-400 rounded-md absolute flex flex-wrap gap-[2px]" style={{ top: top + "%", left: left + "%", width: width + "%", height: height }}>
                        <div className="w-[30px] h-[5px] bg-blue-500 rounded-sm"></div>
                        <div className="w-[30px] h-[5px] bg-red-500 rounded-sm"></div>
                        <div className="w-[30px] h-[5px] bg-green-500 rounded-sm"></div>
                        <div className="w-[30px] h-[5px] bg-yellow-500 rounded-sm"></div>
                    </div>
                </div>
                <div className="flex items-center justify-between text-xs text-theme-1">
                    <p>Today</p>
                    <p>Due</p>
                </div>
            </div>
        </div>
    );
};
